import React, { FC } from 'react';
import { DietaryNutrients } from 'lib/home/generated/types/api';
import { calculateDailyPercentage } from 'lib/util/nutrition';
import NutritionLabelLineItem from './NutritionLabelLineItem';
import classNames from 'classnames';
import './nutrition-label.scss';

export interface NutritionLabelProps {
  className?: string;
  nutrients: DietaryNutrients;
}

export const NutritionLabel: FC<NutritionLabelProps> = ({ className, nutrients }) => {
  const {
    calories,
    fat,
    fat_saturated,
    fat_trans,
    cholesterol,
    sodium,
    carbohydrate,
    fiber,
    sugar,
    added_sugar,
    protein,
    vitamin_d_IU,
    calcium,
    iron,
    potassium,
    vitamin_c,
    vitamin_b6
  } = nutrients;

  return (
    <div className={classNames(`nutritional-label border-2 border-black font-helvetica`, className)}>
      <div className="nutritional-label--serving-data p-2">
        <p className="border-b border-black font-black my-0 text-3xl">Nutrition Facts</p>

        <div className="border-b-4 border-black flex justify-between py-2">
          <p className="my-0 nutrition-label__small-copy">1 serving per container</p>
          <p className="my-0 nutrition-label__small-copy">Serving Size</p>
        </div>

        <div className="border-b-2 border-black flex items-end justify-between">
          <p className="font-black my-0 text-xl">
            <span className="nutrition-label__small-copy">Amount per serving</span>
            <br />
            Calories
          </p>
          <p className="font-bold mb-0 text-3xl">{calories}</p>
        </div>

        <p className="border-b border-black font-bold my-0 py-1 nutrition-label__small-copy text-right">
          % Daily Value *
        </p>

        <NutritionLabelLineItem
          amount={`${fat}g`}
          border={1}
          dailyValue={calculateDailyPercentage('fat', fat)}
          label={<span className="font-bold">Total Fat</span>}
        />

        <NutritionLabelLineItem
          amount={`${fat_saturated}g`}
          border={1}
          dailyValue={calculateDailyPercentage('fat_saturated', fat_saturated)}
          indent={1}
          label="Saturated Fat"
        />

        <NutritionLabelLineItem
          amount={`${fat_trans}g`}
          border={1}
          indent={1}
          label={
            <>
              <span className="italic">Trans</span> Fat
            </>
          }
        />

        <NutritionLabelLineItem
          amount={`${cholesterol}g`}
          border={1}
          dailyValue={calculateDailyPercentage('cholesterol', cholesterol)}
          label={<span className="font-bold">Cholesterol</span>}
        />

        <NutritionLabelLineItem
          amount={`${sodium}g`}
          border={1}
          dailyValue={calculateDailyPercentage('sodium', sodium)}
          label={<span className="font-bold">Sodium</span>}
        />

        <NutritionLabelLineItem
          amount={`${carbohydrate}g`}
          border={1}
          dailyValue={calculateDailyPercentage('carbohydrate', carbohydrate)}
          label={<span className="font-bold">Total Carbohydrate</span>}
        />

        <NutritionLabelLineItem
          amount={`${carbohydrate}g`}
          border={1}
          indent={1}
          dailyValue={calculateDailyPercentage('fiber', fiber)}
          label="Dietary Fiber"
        />

        <NutritionLabelLineItem amount={`${sugar}g`} border={1} indent={1} label="Total Sugars" />
        <NutritionLabelLineItem
          border={1}
          indent={2}
          dailyValue={calculateDailyPercentage('added_sugar', added_sugar)}
          label={`Includes ${added_sugar}g Added Sugars`}
        />

        <NutritionLabelLineItem amount={`${protein}g`} border={4} label={<span className="font-bold">Protein</span>} />
        <NutritionLabelLineItem
          amount={`${vitamin_d_IU}mcg`}
          border={1}
          dailyValue={calculateDailyPercentage('vitamin_d_IU', vitamin_d_IU)}
          label="Vitamin D"
        />

        <NutritionLabelLineItem
          amount={`${calcium}mg`}
          border={1}
          dailyValue={calculateDailyPercentage('calcium', calcium)}
          label="Calcium"
        />

        <NutritionLabelLineItem
          amount={`${iron}mg`}
          border={1}
          dailyValue={calculateDailyPercentage('iron', iron)}
          label="Iron"
        />

        <NutritionLabelLineItem
          amount={`${potassium}mg`}
          border={1}
          dailyValue={calculateDailyPercentage('potassium', potassium)}
          label="Potassium"
        />

        <NutritionLabelLineItem
          amount={`${vitamin_c}mg`}
          border={1}
          dailyValue={calculateDailyPercentage('vitamin_c', vitamin_c)}
          label="Vitamin C"
        />

        <NutritionLabelLineItem
          amount={`${vitamin_b6}mg`}
          border={1}
          dailyValue={calculateDailyPercentage('vitamin_b6', vitamin_b6)}
          label={
            <>
              Vitamin B<sub>6</sub>
            </>
          }
        />

        <p className="nutrition-label__small-copy mb-0 mt-1">
          * The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000
          calories a day is used for general nutrition advice.
        </p>
      </div>
    </div>
  );
};
